import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  History,
  LayoutDashboard,
  List,
  RefreshCw,
  X,
  ChevronRight,
  ShieldAlert,
  Users,
  Activity,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { apiAudit } from '../api/client.js';
import DatePicker from '../components/DatePicker.jsx';

/**
 * Sprint 13 — Vue Audit (lecture seule). Deux sous-vues :
 *  - Tableau de bord : KPI + graphiques Recharts (timeline, catégories, top acteurs).
 *  - Journal : table paginée filtrable (cursor "Charger plus").
 *
 * Graphiques : Recharts (standard graphique du projet). Recharts ne lit pas
 * les variables CSS dans ses props fill/stroke ; on résout donc les variables
 * du thème une fois via getComputedStyle (hook useThemeColors) et on passe les
 * strings résolues. Style et couleurs : exclusivement variables CSS Synolog.
 * Icônes Lucide uniquement.
 */

// ---------- styles partagés ----------

const pill = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  borderRadius: 999,
  border: `1px solid ${active ? 'var(--accent)' : 'var(--glass-border)'}`,
  background: active ? 'var(--accent)' : 'var(--glass)',
  color: active ? '#fff' : 'var(--text)',
  padding: '5px 13px',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
});

const card = {
  background: 'var(--card-bg)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card)',
  padding: 16,
};

const inputStyle = {
  background: 'var(--glass)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text)',
  padding: '5px 9px',
  fontSize: 12,
};

const badge = (bg, color) => ({
  display: 'inline-block',
  background: bg,
  color: color ?? '#fff',
  borderRadius: 999,
  padding: '2px 8px',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.3,
});

// Recharts ne résout pas les `var(--x)` dans ses props fill/stroke : on lit
// les valeurs calculées du thème une fois au mount (et on les recalcule si le
// thème change). Fallback raisonnable si une variable est vide/absente.
const COLOR_FALLBACKS = {
  '--accent': '#326496',
  '--primary': '#2a3147',
  '--success': '#22c55e',
  '--warning': '#f59e0b',
  '--danger': '#ef4444',
  '--text': '#f1f5f9',
  '--text-muted': '#94a3b8',
  '--glass-border': 'rgba(255,255,255,0.1)',
  '--card-bg': '#1e293b',
};

function resolveThemeColors() {
  const cs =
    typeof window !== 'undefined' && document?.documentElement
      ? getComputedStyle(document.documentElement)
      : null;
  const read = (name) => {
    const v = cs ? cs.getPropertyValue(name).trim() : '';
    return v || COLOR_FALLBACKS[name];
  };
  return {
    accent: read('--accent'),
    primary: read('--primary'),
    success: read('--success'),
    warning: read('--warning'),
    danger: read('--danger'),
    text: read('--text'),
    textMuted: read('--text-muted'),
    glassBorder: read('--glass-border'),
    cardBg: read('--card-bg'),
  };
}

/**
 * Hook : couleurs du thème résolues (strings hex/rgba). Recalcule au mount et
 * lorsque la classe de thème (.light / .dark) change sur <html> (MutationObserver).
 */
function useThemeColors() {
  const [colors, setColors] = useState(resolveThemeColors);
  useEffect(() => {
    const update = () => setColors(resolveThemeColors());
    update();
    const html = document.documentElement;
    const obs = new MutationObserver(update);
    obs.observe(html, { attributes: true, attributeFilter: ['class', 'style'] });
    return () => obs.disconnect();
  }, []);
  return colors;
}

/**
 * Palette du camembert dérivée des couleurs d'accent du thème + quelques teintes
 * de complément. Les 5 premières viennent des variables Synolog (accent / primary
 * / success / warning / danger) ; les suivantes sont des teintes neutres/froides
 * choisies pour rester lisibles en thème clair ET sombre (commentées car non
 * issues d'une variable CSS — la palette officielle n'en fournit que 5).
 */
function buildPieColors(c) {
  return [
    c.accent,
    c.primary,
    c.success,
    c.warning,
    c.danger,
    // Teintes de complément (au-delà des 5 variables thème) — pas de variable
    // CSS dédiée, choisies neutres/froides pour rester lisibles dans les 3 thèmes.
    '#5b9bd5',
    '#9b6dbf',
    '#3fa796',
    '#c9772f',
    '#7c8aa5',
  ];
}

function formatDateFr(d) {
  const dt = new Date(d);
  return dt.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isoDaysAgo(days) {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// ---------- graphiques Recharts ----------

// Style commun des tooltips Recharts (lisible en thème clair ET sombre — couleurs
// résolues du thème, jamais de blanc/noir en dur).
function tooltipContentStyle(c) {
  return {
    background: c.cardBg,
    border: `1px solid ${c.glassBorder}`,
    borderRadius: 8,
    color: c.text,
    fontSize: 12,
  };
}

// jj/mm court pour les ticks de l'axe temporel (à partir d'un 'YYYY-MM-DD').
function shortDayTick(day) {
  if (typeof day !== 'string' || day.length < 10) return day;
  return `${day.slice(8, 10)}/${day.slice(5, 7)}`;
}

function BarTimeline({ data, colors }) {
  if (!data.length) return <Empty label="Aucune donnée sur la période" />;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
        <CartesianGrid stroke={colors.glassBorder} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="day"
          tickFormatter={shortDayTick}
          tick={{ fill: colors.textMuted, fontSize: 11 }}
          stroke={colors.glassBorder}
          minTickGap={16}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fill: colors.textMuted, fontSize: 11 }}
          stroke={colors.glassBorder}
          width={36}
        />
        <Tooltip
          contentStyle={tooltipContentStyle(colors)}
          labelStyle={{ color: colors.textMuted }}
          itemStyle={{ color: colors.text }}
          cursor={{ fill: colors.glassBorder }}
          labelFormatter={(day) => `Jour : ${day}`}
          formatter={(value) => [value, 'Actions']}
        />
        <Bar dataKey="count" name="Actions" fill={colors.accent} radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function PieCategories({ data, colors }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  if (!total) return <Empty label="Aucune donnée sur la période" />;
  const palette = buildPieColors(colors);
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={data}
          dataKey="count"
          nameKey="category"
          cx="50%"
          cy="50%"
          outerRadius={80}
          stroke={colors.cardBg}
          strokeWidth={1}
        >
          {data.map((d, i) => (
            <Cell key={d.category} fill={palette[i % palette.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={tooltipContentStyle(colors)}
          itemStyle={{ color: colors.text }}
          formatter={(value, name) => [`${value} (${Math.round((value / total) * 100)}%)`, name]}
        />
        <Legend
          wrapperStyle={{ fontSize: 12, color: colors.text }}
          formatter={(value) => <span style={{ color: colors.text }}>{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

function HBarActors({ data, colors }) {
  if (!data.length) return <Empty label="Aucun acteur sur la période" />;
  return (
    <ResponsiveContainer width="100%" height={Math.max(160, data.length * 28 + 32)}>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid stroke={colors.glassBorder} strokeDasharray="3 3" horizontal={false} />
        <XAxis
          type="number"
          allowDecimals={false}
          tick={{ fill: colors.textMuted, fontSize: 11 }}
          stroke={colors.glassBorder}
        />
        <YAxis
          type="category"
          dataKey="label"
          width={150}
          tick={{ fill: colors.text, fontSize: 11 }}
          stroke={colors.glassBorder}
        />
        <Tooltip
          contentStyle={tooltipContentStyle(colors)}
          itemStyle={{ color: colors.text }}
          cursor={{ fill: colors.glassBorder }}
          formatter={(value) => [value, 'Actions']}
        />
        <Bar dataKey="count" name="Actions" fill={colors.accent} radius={[0, 3, 3, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Empty({ label }) {
  return (
    <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 24, textAlign: 'center' }}>
      {label}
    </div>
  );
}

function KpiCard({ label, value, accent, Icon }) {
  return (
    <div style={{ ...card, padding: 14, minWidth: 150, flex: '1 1 150px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontSize: 12 }}>
        {Icon ? <Icon size={14} color={accent ?? 'var(--text-muted)'} /> : null}
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent ?? 'var(--text)', marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

// ---------- détail metadata ----------

// Libellés FR des clés de metadata les plus courantes (pour un affichage lisible
// du journal). Les clés inconnues sont affichées telles quelles.
const META_KEY_LABELS = {
  managedId: 'Personne gérée',
  managerId: 'Manager',
  personId: 'Personne',
  projectManagerId: 'Chef de projet',
  clientId: 'Client',
  affaireId: 'Affaire',
  departmentId: 'Département',
  roleId: 'Rôle',
  timeOffTypeId: 'Type de congé',
  phaseId: 'Phase',
  addedById: 'Ajouté par',
  createdById: 'Créé par',
  updatedById: 'Modifié par',
  statusCreatorId: 'Validé par',
  occurrenceDate: 'Occurrence',
  wasLinked: 'Lien existant',
  created: 'Créé',
  scopes: 'Scopes',
  scopesGranted: 'Scopes accordés',
  hasOverrides: 'Surcharges',
  hasEmail: 'Email renseigné',
  hasNote: 'Note',
  hasReason: 'Motif',
  personCount: 'Nb de personnes',
  count: 'Nombre',
  ttlSeconds: 'Durée (s)',
  reason: 'Motif',
  lockDate: 'Date de verrou',
  source: 'Origine',
};

const labelForKey = (k) => META_KEY_LABELS[k] ?? k;

// Affiche une valeur scalaire de façon lisible (booléens en Oui/Non).
function displayScalar(v) {
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  return String(v);
}

// Affiche une valeur d'un diff (avant/après) : booléens, vide, objets.
function displayDiffValue(v) {
  if (v == null) return '∅';
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  if (typeof v === 'object') return '(objet)';
  const s = String(v);
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
}

// Côté lisible d'un changement de champ (gère masquage + FK résolus en nom).
function changeSide(ch, side) {
  if (ch && ch.masked) return '•••';
  const labelKey = side === 'from' ? 'fromLabel' : 'toLabel';
  if (ch && ch[labelKey] != null) return ch[labelKey];
  return displayDiffValue(ch ? ch[side] : undefined);
}

/**
 * Formate la metadata d'une ligne d'audit en texte lisible : substitue les
 * identifiants bruts (cuid) par les libellés résolus côté backend (`refs`) et
 * affiche le diff `changes` au format « champ : avant → après ».
 */
function formatMetadataInline(metadata, refs) {
  if (metadata == null) return '—';
  if (typeof metadata !== 'object') return String(metadata);
  const r = refs || {};
  const parts = [];

  // Sujet de l'action (clé générique `id`) en tête, si résolu en nom.
  if (r.id) parts.push(r.id.label);

  const changes =
    metadata.changes && typeof metadata.changes === 'object' && !Array.isArray(metadata.changes)
      ? metadata.changes
      : null;

  if (changes) {
    for (const [field, ch] of Object.entries(changes)) {
      parts.push(`${labelForKey(field)} : ${changeSide(ch, 'from')} → ${changeSide(ch, 'to')}`);
    }
  } else if (Array.isArray(metadata.changedKeys)) {
    parts.push(`Champs modifiés : ${metadata.changedKeys.join(', ') || '—'}`);
  }

  for (const [k, v] of Object.entries(metadata)) {
    if (k === 'id' || k === 'changedKeys' || k === 'changes') continue;
    if (v == null || typeof v === 'object') continue;
    if (r[k]) parts.push(`${labelForKey(k)} : ${r[k].label}`);
    else parts.push(`${labelForKey(k)} : ${displayScalar(v)}`);
  }

  const txt = parts.join(' · ');
  if (!txt) return Object.keys(metadata).length ? '(objet)' : '—';
  return txt.length > 160 ? `${txt.slice(0, 160)}…` : txt;
}

function DetailModal({ row, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ ...card, width: 'min(560px, 100%)', maxHeight: '80vh', overflow: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <strong style={{ color: 'var(--text)' }}>Détail de l'événement</strong>
          <button
            onClick={onClose}
            title="Fermer"
            style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
        </div>
        <dl style={{ margin: 0, fontSize: 13, color: 'var(--text)' }}>
          <Field label="Date" value={formatDateFr(row.createdAt)} />
          <Field label="Action" value={row.action} />
          <Field label="Acteur" value={`${row.actor.label}${row.actor.role ? ` (${row.actor.role})` : ''}`} />
          {row.actor.email ? <Field label="Identifiant" value={row.actor.email} /> : null}
          <Field label="Type" value={row.actor.kind === 'm2m' ? 'API M2M' : row.actor.kind === 'system' ? 'Système' : 'UI'} />
        </dl>
        {row.metadata &&
        typeof row.metadata === 'object' &&
        row.metadata.changes &&
        typeof row.metadata.changes === 'object' ? (
          <>
            <div style={{ marginTop: 12, color: 'var(--text-muted)', fontSize: 12 }}>
              Modifications
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 6 }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px', fontWeight: 500 }}>Champ</th>
                  <th style={{ padding: '4px 8px', fontWeight: 500 }}>Avant</th>
                  <th style={{ padding: '4px 8px', fontWeight: 500 }}>Après</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(row.metadata.changes).map(([field, ch]) => (
                  <tr key={field} style={{ borderTop: '1px solid var(--glass-border)' }}>
                    <td style={{ padding: '4px 8px', color: 'var(--text)' }}>{labelForKey(field)}</td>
                    <td style={{ padding: '4px 8px', color: 'var(--text-muted)' }}>
                      {changeSide(ch, 'from')}
                    </td>
                    <td style={{ padding: '4px 8px', color: 'var(--text)' }}>
                      {changeSide(ch, 'to')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}
        {row.refs && Object.keys(row.refs).length ? (
          <>
            <div style={{ marginTop: 12, color: 'var(--text-muted)', fontSize: 12 }}>
              Références
            </div>
            <dl style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text)' }}>
              {Object.entries(row.refs).map(([k, ref]) => (
                <Field
                  key={k}
                  label={k === 'id' ? 'Élément' : labelForKey(k)}
                  value={ref.label}
                />
              ))}
            </dl>
          </>
        ) : null}
        <div style={{ marginTop: 12, color: 'var(--text-muted)', fontSize: 12 }}>Métadonnées</div>
        <pre
          style={{
            background: 'var(--glass)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-sm)',
            padding: 12,
            fontSize: 12,
            color: 'var(--text)',
            overflow: 'auto',
            margin: '6px 0 0',
          }}
        >
          {JSON.stringify(row.metadata ?? null, null, 2)}
        </pre>
      </div>
    </div>,
    document.body,
  );
}

function Field({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
      <dt style={{ color: 'var(--text-muted)', width: 110, flexShrink: 0 }}>{label}</dt>
      <dd style={{ margin: 0, wordBreak: 'break-word' }}>{value}</dd>
    </div>
  );
}

// ---------- sous-vue dashboard ----------

const PERIOD_PRESETS = [
  { key: 7, label: '7 j' },
  { key: 30, label: '30 j' },
  { key: 90, label: '90 j' },
  { key: 365, label: '365 j' },
];

function DashboardSubview() {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const colors = useThemeColors();

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiAudit.stats({ from: isoDaysAgo(d), to: todayIso() });
      setStats(res);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Période :</span>
        {PERIOD_PRESETS.map((p) => (
          <button key={p.key} style={pill(days === p.key)} onClick={() => setDays(p.key)}>
            {p.label}
          </button>
        ))}
        <button style={{ ...pill(false), marginLeft: 'auto' }} onClick={() => load(days)} title="Rafraîchir">
          <RefreshCw size={13} /> Rafraîchir
        </button>
      </div>

      {error ? (
        <div style={{ ...card, color: 'var(--danger)' }}>{error}</div>
      ) : loading && !stats ? (
        <div style={{ ...card, color: 'var(--text-muted)' }}>Chargement…</div>
      ) : stats ? (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <KpiCard label="Total actions" value={stats.total} Icon={Activity} accent="var(--accent)" />
            <KpiCard label="Acteurs distincts" value={stats.distinctActors} Icon={Users} accent="var(--primary)" />
            <KpiCard label="Archives / suppressions" value={stats.sensitive.archives} Icon={ShieldAlert} accent="var(--warning)" />
            <KpiCard label="Échecs OAuth" value={stats.sensitive.oauthFailures} Icon={ShieldAlert} accent="var(--danger)" />
            <KpiCard label="Changements de verrou" value={stats.sensitive.lockChanges} Icon={ShieldAlert} accent="var(--warning)" />
            <KpiCard label="Refus de congés" value={stats.sensitive.rejects} Icon={ShieldAlert} accent="var(--danger)" />
          </div>

          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>
              Volume d'actions par jour
            </div>
            <BarTimeline data={stats.byDay} colors={colors} />
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ ...card, flex: '1 1 320px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>
                Répartition par catégorie
              </div>
              <PieCategories data={stats.byCategory} colors={colors} />
            </div>
            <div style={{ ...card, flex: '1 1 320px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>
                Top 10 acteurs
              </div>
              <HBarActors data={stats.topActors} colors={colors} />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ---------- sous-vue journal ----------

const SOURCE_OPTIONS = [
  { key: '', label: 'Tout' },
  { key: 'ui', label: 'UI' },
  { key: 'public_api', label: 'API M2M' },
];

function JournalSubview() {
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({ action: '', source: '', from: '', to: '' });
  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    apiAudit
      .actions()
      .then((res) => setActions(res.items || []))
      .catch(() => setActions([]));
  }, []);

  const queryParams = useMemo(
    () => ({
      action: filters.action || undefined,
      source: filters.source || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
    }),
    [filters],
  );

  const loadFirst = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiAudit.list({ ...queryParams, limit: 50 });
      setRows(res.items || []);
      setNextCursor(res.nextCursor);
      setHasMore(res.hasMore);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    loadFirst();
  }, [loadFirst]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoading(true);
    try {
      const res = await apiAudit.list({ ...queryParams, limit: 50, cursor: nextCursor });
      setRows((prev) => [...prev, ...(res.items || [])]);
      setNextCursor(res.nextCursor);
      setHasMore(res.hasMore);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Barre de filtres */}
      <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Action :</span>
          <select style={inputStyle} value={filters.action} onChange={(e) => setFilter('action', e.target.value)}>
            <option value="">Toutes</option>
            {actions.map((a) => (
              <option key={a.action} value={a.action}>
                {a.action} ({a.count})
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Source :</span>
          {SOURCE_OPTIONS.map((s) => (
            <button key={s.key} style={pill(filters.source === s.key)} onClick={() => setFilter('source', s.key)}>
              {s.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Du :</span>
          <DatePicker style={{ ...inputStyle, width: 'auto' }} value={filters.from} onChange={(v) => setFilter('from', v)} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>au :</span>
          <DatePicker style={{ ...inputStyle, width: 'auto' }} value={filters.to} onChange={(v) => setFilter('to', v)} />
        </div>
        {(filters.action || filters.source || filters.from || filters.to) && (
          <button
            style={pill(false)}
            onClick={() => setFilters({ action: '', source: '', from: '', to: '' })}
          >
            <X size={12} /> Réinitialiser
          </button>
        )}
      </div>

      {error ? <div style={{ ...card, color: 'var(--danger)' }}>{error}</div> : null}

      {/* Tableau */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-muted)', borderBottom: '1px solid var(--glass-border)' }}>
              <th style={{ padding: '10px 12px' }}>Date</th>
              <th style={{ padding: '10px 12px' }}>Acteur</th>
              <th style={{ padding: '10px 12px' }}>Action</th>
              <th style={{ padding: '10px 12px' }}>Détail</th>
              <th style={{ padding: '10px 12px', width: 30 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => setDetail(r)}
                style={{ borderBottom: '1px solid var(--glass-border)', cursor: 'pointer' }}
              >
                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                  {formatDateFr(r.createdAt)}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {r.actor.kind === 'm2m' ? (
                      <span style={badge('var(--primary)')}>M2M</span>
                    ) : r.actor.kind === 'system' ? (
                      <span style={badge('var(--text-muted)')}>SYS</span>
                    ) : (
                      <span style={badge('var(--accent)')}>UI</span>
                    )}
                    <div>
                      <div style={{ color: 'var(--text)' }}>{r.actor.label}</div>
                      {r.actor.role ? (
                        <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>{r.actor.role}</div>
                      ) : null}
                    </div>
                  </div>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <span style={badge('var(--glass)', 'var(--text)')}>{r.action}</span>
                </td>
                <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>
                  {formatMetadataInline(r.metadata, r.refs)}
                </td>
                <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>
                  <ChevronRight size={14} />
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <Empty label="Aucun événement pour ces filtres" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {hasMore ? (
          <button style={pill(false)} onClick={loadMore} disabled={loading}>
            {loading ? 'Chargement…' : 'Charger plus'}
          </button>
        ) : rows.length > 0 ? (
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Fin de la liste</span>
        ) : null}
      </div>

      {detail ? <DetailModal row={detail} onClose={() => setDetail(null)} /> : null}
    </div>
  );
}

// ---------- vue racine ----------

export default function AuditView() {
  const [tab, setTab] = useState('dashboard');

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <History size={22} color="var(--accent)" />
        <h1 style={{ margin: 0, fontSize: 20, color: 'var(--text)' }}>Audit</h1>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <button style={pill(tab === 'dashboard')} onClick={() => setTab('dashboard')}>
            <LayoutDashboard size={13} /> Tableau de bord
          </button>
          <button style={pill(tab === 'journal')} onClick={() => setTab('journal')}>
            <List size={13} /> Journal
          </button>
        </div>
      </div>

      {tab === 'dashboard' ? <DashboardSubview /> : <JournalSubview />}
    </div>
  );
}
